import { Box, type BoxProps } from "@mui/material";

import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

export const ORION_BORDER_CARD_IMAGE_URLS = {
  multiColorAbstractHex:
    "https://cdn.queen.one/connect/ui/multi-color-abstract-hex-bg.png",
  purpleAbstractHex:
    "https://cdn.queen.one/connect/ui/purple-abstract-hex-bg.png",
  purpleAbstractHexTealTop:
    "https://cdn.queen.one/connect/ui/purple-abstract-hex-bg-teal-top.png",
  purpleAbstractHexTealRight:
    "https://cdn.queen.one/connect/ui/purple-abstract-hex-bg-teal-right.png",
  abstractOvalHex:
    "https://cdn.queen.one/connect/ui/abstract-oval-hex-background.png",
} as const;

export const ORION_BORDER_CARD_ROTATING_IMAGE_URLS = [
  ORION_BORDER_CARD_IMAGE_URLS.multiColorAbstractHex,
  ORION_BORDER_CARD_IMAGE_URLS.purpleAbstractHexTealTop,
  ORION_BORDER_CARD_IMAGE_URLS.purpleAbstractHexTealRight,
] as const;

export interface OrionBorderCardProps extends Omit<BoxProps, "children"> {
  children: ReactNode;
  backgroundImage: string;
  innerSx?: SxProps<Theme>;
  innerBackgroundColor?: string;
  padding?: number;
  borderRadius?: number;
  innerBorderRadius?: number;
  hideBottomBorder?: boolean;
}

export function OrionBorderCard({
  children,
  backgroundImage,
  innerSx,
  innerBackgroundColor = "background.paper-elevation-0",
  padding = 1,
  borderRadius = 1,
  innerBorderRadius = 1,
  hideBottomBorder = false,
  sx,
  ...boxProps
}: OrionBorderCardProps) {
  const outerBaseSx: SxProps<Theme> = {
    borderRadius,
    p: padding,
    position: "relative",
    overflow: "hidden",
    background: `url('${backgroundImage}') no-repeat center center`,
    backgroundSize: "cover",
    ...(hideBottomBorder && {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      pb: 0,
    }),
  };

  const resolvedOuterSx: SxProps<Theme> = sx
    ? ([outerBaseSx, sx] as SxProps<Theme>)
    : outerBaseSx;

  const innerBaseSx: SxProps<Theme> = {
    borderRadius: innerBorderRadius,
    backgroundColor: innerBackgroundColor,
    position: "relative",
    zIndex: 1,
    ...(hideBottomBorder && {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    }),
  };

  const resolvedInnerSx: SxProps<Theme> = innerSx
    ? ([innerBaseSx, innerSx] as SxProps<Theme>)
    : innerBaseSx;

  return (
    <Box {...boxProps} sx={resolvedOuterSx}>
      <Box sx={resolvedInnerSx}>{children}</Box>
    </Box>
  );
}
